import { useCallback, useEffect, useState, useTransition, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import { ContentContainer } from '../../../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../../../components/Filters/components/CheckboxList';
import { SearchInput } from '../../../../../components/Filters/components/SearchInput';
import { ItemBadgesContainer } from '../../../../../components/Filters/components/ItemBadgesContainer';
import { FilterLoader } from '../../../../../components/Filters/components/FilterLoader';
import { NoResults } from '../../../../../components/Filters/components/NoResults';
import { API, ShareAPI } from '../../../../../API';
import { useTranslation } from 'react-i18next';
import { useBreakdownsFilterContext } from '../BreakdownsFilterContext';

export const AffiliationCountriesChooser = () => {
  const { t } = useTranslation();
  const { state, isShare, updateDraftFilters } = useBreakdownsFilterContext();
  const api = isShare ? ShareAPI : API;
  const [countries, setCountries] = useState([]);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [allLoadedCountries, setAllLoadedCountries] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isPending, startTransition] = useTransition();

  const isAllSelected = state.draftFilters.affiliations.value.length > 0 && state.draftFilters.affiliations.value.includes('all');
  const selectedCountries = isAllSelected ? ['all'] : state.draftFilters.affiliations.value.map(c => parseInt(c));

  const getAllCountries = useCallback(() => {
    startTransition(() => {
      api.fetch('GET', '/API/v1/countries')
        .then((data) => {
          const optionsList = data.objects.slice(0, 10).map((country) => ({
            value: country.id,
            label: country.name,
          }));
          setDefaultOptions(optionsList);
          setAllLoadedCountries(prev => {
            const existingIds = new Set(prev.map(c => c.value));
            const newCountries = optionsList.filter(c => !existingIds.has(c.value));
            return [...prev, ...newCountries];
          });
        })
        .catch((error) => console.error('Failed to fetch countries:', error));
    });
  }, [api]);

  useEffect(getAllCountries, [getAllCountries]);

  useEffect(() => {
    if (selectedCountries.length === 0 || isAllSelected) return;
    const missingIds = selectedCountries.filter(id => !allLoadedCountries.some(c => c.value === id));
    if (missingIds.length === 0) return;

    missingIds.forEach(countryId => {
      api.fetch('GET', `/API/v1/countries/${countryId}`)
        .then((country) => {
          setAllLoadedCountries(prev => {
            if (prev.some(c => c.value === country.id)) return prev;
            return [...prev, { value: country.id, label: country.name }];
          });
        })
        .catch((error) => console.error(`Failed to fetch country ${countryId}:`, error));
    });
  }, [selectedCountries.length, isAllSelected, api]);

  const getCountries = useCallback((inputValue) => {
    if (!inputValue) {
      setCountries([]);
      return;
    }
    startTransition(() => {
      api.fetch('GET', `/API/v1/countries?q=${inputValue}`)
        .then((data) => {
          const optionsList = data.objects.map((country) => ({
            value: country.id,
            label: country.name,
          }));
          setCountries(optionsList);
          setAllLoadedCountries(prev => {
            const existingIds = new Set(prev.map(c => c.value));
            const newCountries = optionsList.filter(c => !existingIds.has(c.value));
            return [...prev, ...newCountries];
          });
        })
        .catch((error) => console.error('Failed to fetch countries:', error));
    });
  }, [api]);

  const debouncedSearchRef = useRef(debounce((value) => getCountries(value), 500));

  useEffect(() => {
    debouncedSearchRef.current = debounce((value) => getCountries(value), 500);
  }, [getCountries]);

  useEffect(() => {
    const debouncedSearch = debouncedSearchRef.current;
    return () => debouncedSearch.cancel();
  }, []);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    debouncedSearchRef.current(value);
  };

  const handleClearAll = () => {
    updateDraftFilters({ affiliations: { value: [], exclude: false } });
  };

  const handleToggleCountry = useCallback((countryId) => {
    const currentCountries = state.draftFilters.affiliations.value;
    const countryIdStr = String(countryId);

    if (currentCountries.includes(countryIdStr)) {
      updateDraftFilters({
        affiliations: {
          value: currentCountries.filter(id => id !== countryIdStr),
          exclude: state.draftFilters.affiliations.exclude,
        },
      });
    } else {
      updateDraftFilters({
        affiliations: {
          value: [...currentCountries, countryIdStr],
          exclude: state.draftFilters.affiliations.exclude,
        },
      });
    }
  }, [state.draftFilters.affiliations, updateDraftFilters]);

  const listToShow = countries.length > 0 ? countries : defaultOptions;
  const isNoResults = !isPending && searchValue && countries.length === 0;

  const checkboxItems = useMemo(() => {
    return listToShow.map((country) => ({
      name: country.label,
      checked: selectedCountries.includes(typeof country.value === 'string' ? parseInt(country.value) : country.value),
      onChange: () => handleToggleCountry(country.value),
      disabled: false,
      icon: null,
    }));
  }, [listToShow, selectedCountries, handleToggleCountry]);

  const badges = useMemo(() => {
    if (isAllSelected) {
      return [{
        name: 'All',
        onChange: () => updateDraftFilters({
          affiliations: { value: [], exclude: state.draftFilters.affiliations.exclude },
        }),
      }];
    }
    return selectedCountries.map(countryId => {
      const country = allLoadedCountries.find(c => c.value === countryId);
      return {
        name: country ? country.label : String(countryId),
        onChange: () => handleToggleCountry(countryId),
      };
    });
  }, [isAllSelected, selectedCountries, allLoadedCountries, handleToggleCountry, updateDraftFilters, state.draftFilters.affiliations.exclude]);

  return (
    <ContentContainer
      searchComponent={
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('Search countries')}
          hint={t('Use search to find options not shown in the list')}
          id="breakdown-affiliation-country-search"
          isDisabled={isAllSelected}
        />
      }
      actionsComponent={
        <ControlsPanel
          id="breakdown-affiliation-country-filter"
          count={selectedCountries.length}
          isExclude={state.draftFilters.affiliations.exclude}
          showExcludeSwitch={true}
          showSelectAll={true}
          onClearAll={handleClearAll}
          isSelectAllDisabled={isAllSelected}
          onExcludeClick={() => {
            updateDraftFilters({
              affiliations: {
                value: state.draftFilters.affiliations.value,
                exclude: !state.draftFilters.affiliations.exclude,
              },
            });
          }}
          onSelectAll={() => {
            updateDraftFilters({
              affiliations: {
                value: ['all'],
                exclude: state.draftFilters.affiliations.exclude,
              },
            });
          }}
        />
      }
      badgesComponent={selectedCountries.length > 0 ? <ItemBadgesContainer badges={badges} /> : undefined}
    >
      {isPending ? <FilterLoader /> : isNoResults ? <NoResults /> : isAllSelected ? null : (
        <CheckboxList items={checkboxItems} />
      )}
    </ContentContainer>
  );
};
